"use client";

import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/* The wallet's two forms ask for the same three things — an address, a hash and
   an amount — so the field and the message under it are shared rather than
   restyled per panel. The geometry is the kit's 42px input: elevation-2 fill,
   border-input edge, brand on focus. */

export const FIELD_CLASS =
  "h-[42px] rounded-lg border border-border-input bg-elevation-2 px-[12px] py-[8px] text-[12px] font-bold leading-[1.6] text-fg outline-none transition-colors focus:border-brand";

export function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  inputMode,
  numeric,
  error,
  action,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputMode?: "decimal" | "text";
  numeric?: boolean;
  error?: string | null;
  action?: ReactNode;
}) {
  return (
    <>
      <label htmlFor={id} className="text-[12px] font-bold leading-[1.6] text-fg-subtle">
        {label}
      </label>

      <div className="relative flex items-center">
        <input
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          inputMode={inputMode}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          className={cn(
            FIELD_CLASS,
            "w-full",
            numeric && "tabular-nums",
            Boolean(action) && "pr-[64px]",
            error && "border-error",
          )}
        />
        {action && <span className="absolute right-[6px]">{action}</span>}
      </div>

      {error && (
        <p role="alert" className="text-[9px] leading-[1.5] text-error-fg">
          {error}
        </p>
      )}
    </>
  );
}

export function Note({ tone, children }: { tone: "success" | "error" | "muted"; children: ReactNode }) {
  return (
    <p
      role={tone === "error" ? "alert" : undefined}
      className={cn(
        "mt-[8px] rounded-lg bg-elevation-2 px-[12px] py-[8px] text-[12px] leading-[1.6]",
        tone === "success" && "text-success-fg",
        tone === "error" && "text-error-fg",
        tone === "muted" && "text-fg-subtle",
      )}
    >
      {children}
    </p>
  );
}
